/// <amd-module name="Traceability/WorkItems/WorkItemsCard" />
import React = require("react");
import { ITraceabilityCardProps, WorkItemRef } from "Traceability/Common/Contracts";
import { TraceabilityBaseCard } from "Traceability/Common/TraceabilityBaseCard";
import * as Resources from "Traceability/WorkItems/Resources";
import { IWorkItemsCardProps, WorkItemStateColor, WorkItemType, WorkItemTypeColorAndIcon } from "Traceability/WorkItems/Types";
import { ObservableValue } from "VSS/Core/Observable";
import { ScreenBreakpoints } from "VSS/Core/Util/Screen";
import * as StringUtils from "VSS/Core/Util/String";
import { localeFormat } from "VSS/Core/Util/String";
import { IdentityRef } from "VSS/Platform/Contracts/WebApi";
import { onClickFPS } from "VSS/Platform/FPS";
import { IVssComponentContext } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";
import { Ago } from "VSSUI/Ago";
import { ICardTitleProps } from "VSSUI/Card";
import { IAnchorProps } from "VSSUI/Link";
import { Observer } from "VSSUI/Observer";
import { IStatusProps, Status, Statuses, StatusSize } from "VSSUI/Status";
import { ITableBreakpoint, ITableColumn, ITableRow, SimpleTableCell, Table } from "VSSUI/Table";
import { Tooltip } from "VSSUI/TooltipEx";
import { AgoFormat } from "VSSUI/Utilities/Date";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";
import { VssPersona } from "VSSUI/VssPersona";
import { TypeIconSize, WorkItemTypeIcon } from "Wit/UI/WorkItemTypeIcon";

interface WorkItemExtendedRef extends WorkItemRef {
    workItemType?: WorkItemType;
    linkProps?: IAnchorProps;
}

export class WorkItemsCard extends TraceabilityBaseCard<WorkItemRef> {
    public constructor(props: IWorkItemsCardProps<WorkItemRef>, context: IVssComponentContext) {
        super(props as ITraceabilityCardProps<WorkItemRef>, context);
        this._workItemTypes = new ObservableValue([]);
        if (!!props.workItemTypes) {
            // workItemTypes property has data of types or a promise to get the required data.
            if ((props.workItemTypes as Partial<Promise<WorkItemType[]>>).then) {
                (props.workItemTypes as Promise<WorkItemType[]>).then((types: WorkItemType[]) => {
                    this._workItemTypes.value = types || [];
                });
            }
            else {
                this._workItemTypes.value = props.workItemTypes as WorkItemType[];
            }
        }
    }

    protected renderTable(context: IVssComponentContext, displayCount: number, cardTitleProps: ICardTitleProps): JSX.Element {
        // zero data length or data is not an array, then take zero as the length
        this._cardData.data = this._cardData.data || [];
        const dataLength = this._cardData.data!.length; // data null check done already
        const currentDisplayCount = Math.min(dataLength, displayCount);
        if (currentDisplayCount <= 0) {
            return <div>{this._cardData.innerErrorMessage ? Resources.NotAbleToFetchWorkItems : Resources.NoWorkItemsText}</div>;
        }

        const ariaLabelForTable = !!cardTitleProps && cardTitleProps.text ? localeFormat(Resources.WorkItemsCardAriaLabel, cardTitleProps.text) : Resources.WorkItemsPageTitle;

        return (
            <Observer workItemTypes={this._workItemTypes}>
                {
                    (props: { workItemTypes: WorkItemType[]; }) => {
                        const workItems = this._cardData.data!.slice(0, currentDisplayCount); // data null check done already
                        const workItemsProvider = new ArrayItemProvider<WorkItemExtendedRef>(
                            workItems.map<WorkItemExtendedRef>(workItem => {
                                return {
                                    ...workItem,
                                    linkProps: {href: workItem.url} as IAnchorProps,
                                    workItemType: (props.workItemTypes || []).find(workItemType => StringUtils.equals(workItemType && workItemType.name || "", workItem && workItem.type || "", true))
                                } as WorkItemExtendedRef;
                            })
                        );

                        return (
                            <Table<WorkItemExtendedRef>
                                className="workitems-table"
                                key={workItemsProvider.length}
                                columns={this._columns}
                                itemProvider={workItemsProvider}
                                rowHeight={64}
                                spacerWidth={9}
                                tableBreakpoints={this._tableBreakpoints} 
                                onActivate={this._onRowClicked}
                                ariaLabel={ariaLabelForTable}
                            />
                        );
                    }
                }
            </Observer>
        );
    }

    private _renderWorkItemColumn = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<WorkItemExtendedRef>, tableItem: WorkItemExtendedRef): JSX.Element => {
        return (
            <SimpleTableCell
                key={"workitems-col-" + columnIndex}
                columnIndex={columnIndex}
                tableColumn={tableColumn}
                children={
                    <>
                        {this._getWorkItemIcon(tableItem.workItemType)}
                        <div className="flex-column scroll-hidden">
                            <Tooltip overflowOnly={true} text={tableItem.title}>
                                <div className="font-weight-semibold body-m text-ellipsis">
                                    {tableItem.title}
                                </div>
                            </Tooltip>
                            <div className="body-s secondary-text">
                                {tableItem.type} {tableItem.id}
                            </div>
                        </div>
                    </>
                }
            />
        );
    }

    private _getWorkItemIcon = (workItemType?: WorkItemType): JSX.Element => {
        let iconData: WorkItemTypeColorAndIcon | undefined = undefined;
        if (workItemType) {
            iconData = {
                color: workItemType.color,
                icon: workItemType.icon && workItemType.icon.id,
                workItemTypeName: workItemType.name,
                isDisabled: workItemType.isDisabled
            }
        }

        // show default icon in case we do not have icon data
        return <WorkItemTypeIcon data={iconData} size={TypeIconSize.large} className="icon-large-margin" />;
    }

    private _renderStateColumn = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<WorkItemExtendedRef>, tableItem: WorkItemExtendedRef): JSX.Element => {
        return (
            <SimpleTableCell key={"commits-col-" + columnIndex} columnIndex={columnIndex} tableColumn={tableColumn}>
                <Tooltip text={tableItem.currentState}>
                    <span className="flex-row">
                        <Status
                            {...this._getStatusProps(tableItem)}
                            ariaLabel={tableItem.currentState}
                            className="icon-large-margin"
                            size={StatusSize.m}
                        />
                    </span>
                </Tooltip>
                <div className="flex-row scroll-hidden">
                    <Tooltip overflowOnly={true} text={tableItem.currentState}>
                        <span className={"body-m text-ellipsis"}>{tableItem.currentState}</span>
                    </Tooltip>
                </div>
            </SimpleTableCell>
        );
    }

    private _getStatusProps(workItem: WorkItemExtendedRef): IStatusProps {
        let currentState: string = workItem.currentState;
        const workItemType = workItem.workItemType;
        if (workItemType) {
            const typeState = (workItemType.states || []).find((state: WorkItemStateColor) => StringUtils.equals(state && state.name || "", workItem.currentState, true));
            if (typeState) {
                currentState = typeState.category;
            }
        }

        switch (currentState.toLowerCase()) {
            case "proposed":
                return Statuses.Queued;
            case "inprogress":
                return Statuses.Running;
            case "open":
                return Statuses.Running;
            case "resolved":
                return Statuses.Success;
            case "completed":
                return Statuses.Success;
            case "closed":
                return Statuses.Success;
            case "removed":
                return Statuses.Canceled;
        }

        return Statuses.Queued;
    }

    private _renderAssignedToColumn = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<WorkItemExtendedRef>, tableItem: WorkItemExtendedRef): JSX.Element => {
        let assignedTo: IdentityRef = tableItem.assignedTo;
        if (!assignedTo) {
            assignedTo = {
                id: "",
                uniqueName: "",
                displayName: Resources.Unassigned_Identity,
                imageUrl: getAssetLocation(
                    this.context.pageContext,
                    this._notAssignedIconLocation
                )
            } as IdentityRef;
        }

        return (
            <SimpleTableCell
                key={"commits-col-" + columnIndex}
                columnIndex={columnIndex}
                tableColumn={tableColumn}
            >
                <VssPersona
                    size={"small"}
                    className={"icon-large-margin"}
                    identityDetailsProvider={{
                        getIdentityImageUrl: (size: number): string => {
                            return assignedTo.imageUrl;
                        },
                        getDisplayName: (): string => {
                            return assignedTo.displayName;
                        }
                    }}
                />
                <div className="flex-row scroll-hidden">
                    <Tooltip overflowOnly={true} text={assignedTo.displayName}>
                        <span className={"body-m text-ellipsis"}>
                            {assignedTo.displayName}
                        </span>
                    </Tooltip>
                </div>
            </SimpleTableCell>
        );
    };

    private _renderDateColumn = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<WorkItemExtendedRef>, tableItem: WorkItemExtendedRef): JSX.Element => {
        return (
            <SimpleTableCell
                key={"commits-col-" + columnIndex}
                columnIndex={columnIndex}
                tableColumn={tableColumn}
                children={
                    <div className={"body-m text-ellipsis"}>
                        <Ago format={AgoFormat.Compact} date={tableItem.createdOn} />
                    </div>
                }
            />
        );
    };

    private _onRowClicked = (event: any, row: ITableRow<WorkItemExtendedRef>): void => {
        onClickFPS(this.context.pageContext, row.data.url, true, event);
    };

    private _columns: ITableColumn<WorkItemExtendedRef>[] = [
        {
            id: "workitem",
            name: Resources.WorkItemColumnName,
            width: new ObservableValue(-55),
            renderCell: this._renderWorkItemColumn
        },
        {
            id: "state",
            name: Resources.StateColumnName,
            width: new ObservableValue(152),
            renderCell: this._renderStateColumn
        },
        {
            id: "assignedTo",
            name: Resources.AssignedToColumnName,
            width: new ObservableValue(-25),
            renderCell: this._renderAssignedToColumn
        },
        {
            id: "date",
            name: Resources.DataColumnName,
            width: new ObservableValue(-20),
            renderCell: this._renderDateColumn
        }
    ];

    private _tableBreakpoints: ITableBreakpoint[] = [
        {
            breakpoint: ScreenBreakpoints.xsmall,
            columnWidths: [-55, 0, 0, 0]
        },
        {
            breakpoint: ScreenBreakpoints.small,
            columnWidths: [-40, -35, -25, 0]
        },
        {
            breakpoint: 800,
            columnWidths: [-35, -35, -25, -20]
        }
    ];

    private _workItemTypes: ObservableValue<WorkItemType[]>;
    private readonly _notAssignedIconLocation = "ms.vss-traceability-web/traceability-workitems/Content/notassigned-user.svg";
}
